/* storybook-check-ignore */
import { Box, BoxProps, Button, Icon, Link } from '@opendoor/bricks/core';
import Slider, { Settings as SliderSettings } from 'react-slick';
import { ListingWithComputedProperties } from '../../../declarations/exclusives/listing';
import GalleryCard from './GalleryCard';
import { getListingThumbnail } from '../../../helpers/exclusives/listing';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import ChevronLeft from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronLeft';
import ChevronRight from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronRight';

export type BaseGallerySliderProps = {
  sliderSettings?: SliderSettings;
  appPrefix?: 'exclusive-offers' | 'exclusive';
  analyticsPrefix?: string;
  'aria-label'?: string;
  children: JSX.Element[];
};

export const DEFAULT_SLIDER_SETTINGS: SliderSettings = {
  dots: false,
  infinite: true,
  speed: 200,
  className: 'center',
  centerMode: true,
  centerPadding: '60px',
  slidesToShow: 4,
  autoplay: false,
  draggable: true,
  arrows: true,
  responsive: [
    {
      breakpoint: 1450,
      settings: {
        speed: 300,
        autoplay: false,
        draggable: true,
        slidesToShow: 3,
        className: 'center',
        centerMode: true,
        centerPadding: '60px',
      },
    },
    {
      breakpoint: 900,
      settings: {
        speed: 300,
        autoplay: false,
        draggable: true,
        slidesToShow: 2,
        className: 'center',
        centerMode: true,
        centerPadding: '60px',
      },
    },
    {
      breakpoint: 600,
      settings: {
        speed: 300,
        autoplay: false,
        draggable: true,
        slidesToShow: 1,
        className: 'center',
        centerMode: true,
        centerPadding: '60px',
      },
    },
  ],
};

export const BaseGallerySlider: React.FC<BaseGallerySliderProps & BoxProps> = ({
  sliderSettings,
  appPrefix,
  analyticsPrefix,
  children,
  'aria-label': ariaLabel,
  ...boxProps
}) => {
  if (children.length === 1) {
    return (
      <Box
        display="grid"
        gridTemplateColumns={[
          null,
          'repeat(2, 1fr)',
          'repeat(2, 1fr)',
          'repeat(3, 1fr)',
          'repeat(4, 1fr)',
        ]}
        gridGap="2rem"
        paddingY="1.5rem"
        paddingX="0.5rem"
        w="100%"
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      overflow="hidden"
      position="relative"
      aria-label={ariaLabel}
      sx={{
        '.slick-dots': {
          position: 'unset',
          li: {
            mx: 0,
            button: {
              _before: { fontSize: 's000' },
            },
          },
        },
      }}
      {...boxProps}
    >
      <Slider
        {...DEFAULT_SLIDER_SETTINGS}
        {...sliderSettings}
        nextArrow={
          <Arrow direction="Next" analyticsPrefix={analyticsPrefix} appPrefix={appPrefix} />
        }
        prevArrow={
          <Arrow direction="Previous" analyticsPrefix={analyticsPrefix} appPrefix={appPrefix} />
        }
      >
        {children}
      </Slider>
    </Box>
  );
};

type GalleryListingsProps = Pick<BaseGallerySliderProps, 'sliderSettings' | 'analyticsPrefix'> &
  BoxProps & {
    listings: ListingWithComputedProperties[];
    sharingSource?: 'gallery' | 'pdp';
    onNotifiableEventSuccess?: (message?: string) => void;
    onNotifiableEventError?: (message?: string) => void;
    shouldHyperlinkCards?: boolean;
  };

const GallerySlider: React.FC<GalleryListingsProps> = ({
  listings,
  sharingSource,
  onNotifiableEventError,
  onNotifiableEventSuccess,
  shouldHyperlinkCards = true,
  ...props
}) => (
  <BaseGallerySlider {...props}>
    {listings.map((detail, i) => (
      <Box key={`${detail.id}-${i}`} px={2} pb={3}>
        <Link
          href={
            shouldHyperlinkCards
              ? `/exclusives/homes/${encodeURIComponent(detail.slug)}`
              : undefined
          }
          aria-label={null}
          analyticsName={`cosmos-exclusive-slider-gallery${
            props?.analyticsPrefix ? '-' + props.analyticsPrefix : ''
          }-card`}
        >
          <GalleryCard
            url={getListingThumbnail(detail, '_760x480')}
            listing={detail}
            sharingSource={sharingSource}
            onNotifiableEventSuccess={onNotifiableEventSuccess}
            onNotifiableEventError={onNotifiableEventError}
          />
        </Link>
      </Box>
    ))}
  </BaseGallerySlider>
);

const Arrow: React.FC<
  BoxProps &
    Pick<BaseGallerySliderProps, 'analyticsPrefix' | 'appPrefix'> & {
      direction: 'Previous' | 'Next';
    }
> = ({ direction, analyticsPrefix, appPrefix, ...props }) => (
  <Box
    zIndex={4}
    top="40%"
    position="absolute"
    _before={{ display: 'none' }}
    left={direction === 'Previous' ? [7, null, null, 9] : 'initial'}
    right={direction === 'Next' ? [9, null, null, '56px'] : 'initial'}
    {...props}
  >
    <SelectiveSpritesheet icons={[ChevronLeft, ChevronRight]} />
    <Button
      type="button"
      variant="icon"
      aria-label={`${direction} slide`}
      onClick={() => {}}
      analyticsName={`cosmos-${appPrefix}-slider-gallery${
        analyticsPrefix ? '-' + analyticsPrefix : undefined
      }-${direction === 'Previous' ? 'left' : 'right'}`}
    >
      <Icon name={direction === 'Previous' ? 'chevron-left' : 'chevron-right'} />
    </Button>
  </Box>
);

export default GallerySlider;
